import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import CircularProgress from "@material-ui/core/CircularProgress";
import MenuItem from "@material-ui/core/MenuItem";
import Tab from "@material-ui/core/Tab";
import Tabs from "@material-ui/core/Tabs";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import DeleteIcon from "@material-ui/icons/Delete";

import SelectorBases from "./selectorBases";
import SelectorCompanies from "./selectorCompanies";
import { adminScreenFrame, BORDER, DANGER } from "./settingsPalette";
import {
  PERMISSION_OPTIONS,
  INTERNAL_PERMISSION_OPTIONS,
  LEGACY_TAB_OPTIONS,
  PLAY_ENGINE_TAB_OPTIONS,
  PE3D_TAB_OPTIONS,
  ROLE_OPTIONS,
  findUsers,
  insertUser,
  updateUser,
  deleteUser,
  findCompanies,
  findAllBases,
} from "../../modules/adminApi";

/**
 * User management, for both admin tiers.
 *
 * A companyAdmin manages its own company. A superAdmin gets a company selector
 * on top of the same screen, plus the things only it can hand out: the
 * superAdmin role itself, the cross-company lists (which companies a user may
 * switch into, and which it may export into), and runtime base management.
 *
 * The previous screen could only create and delete — there was no way to change
 * an existing user's permissions from the dashboard at all, and two permissions
 * could only be granted by running a script. Everything is editable here.
 *
 * Layout: the page is exactly the height of the drawer and never grows, because
 * the editor is long enough that a page-height version put Save a full screen
 * below the fold. The list and the editor scroll separately, the editor is split
 * into tabs, and the action bar is pinned. Each tab label carries a summary
 * ("Permissions · 6", "Bases · all") so the state of a tab is readable without
 * opening it.
 *
 * Base access has two levels and the difference matters, so the editor says it
 * outright: a user reaches everything its company holds unless it is explicitly
 * narrowed, and narrowing is stored as a list which is intersected with the
 * company's grant. Turning the switch off removes the narrowing rather than
 * emptying it, because an empty list means "nothing".
 */

const useStyles = makeStyles(() => ({
  ...adminScreenFrame,

  // Two columns of permissions, and no more than two. The hints run to a full
  // sentence, and a third column at this pane width breaks each one over four
  // lines.
  permissionGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: "2px 20px",
  },

  // Playin's own tools, fenced off from the permissions above them. The rule and
  // the warning-coloured heading are the whole separation — a boxed panel here
  // would outweigh the six permissions it sits under.
  internalBlock: { marginTop: "1.25rem", paddingTop: "0.75rem", borderTop: `1px solid ${BORDER}` },
  internalTitle: { fontSize: "0.72rem", letterSpacing: 0.5, textTransform: "uppercase", color: DANGER },

  // The tabs a narrowing actually selects, indented under the switch that turns
  // it on, so the two read as one control rather than two lists of checkboxes.
  tabChecklist: { marginLeft: "0.5rem", borderLeft: `1px solid ${BORDER}`, paddingLeft: "0.5rem" },
}));

const DEFAULT_NEW_PERMISSIONS = ["viewContent"];
const NEW_USER = "__new__";
const TAB = { ACCOUNT: 0, PERMISSIONS: 1, BASES: 2, EDITOR_TABS: 3, COMPANIES: 4 };

export default function UserManagement(props) {
  const classes = useStyles();

  const viewerRole = localStorage.getItem("user.role") || "member";
  const isSuper = viewerRole === "superAdmin";
  const homeCompanyID = localStorage.getItem("user.homeCompanyID");

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [companyID, setCompanyID] = useState(homeCompanyID);
  const [users, setUsers] = useState([]);
  const [companyBases, setCompanyBases] = useState([]);
  const [selectedUserID, setSelectedUserID] = useState(null);
  const [activeTab, setActiveTab] = useState(TAB.ACCOUNT);

  // The edit form. `restrictBases` is the switch between "everything the company
  // holds" and an explicit list.
  const [draft, setDraft] = useState(null);

  const selectedUser = users.find((user) => String(user._id) === String(selectedUserID)) || null;

  const draftFromUser = (user) => ({
    username: user ? user.username : "",
    password: "",
    role: user ? user.role || "member" : "member",
    permissions: user ? user.permissions || [] : DEFAULT_NEW_PERMISSIONS,
    restrictBases: user ? Boolean(user.isBaseRestricted) : false,
    bases: user ? (user.basesWithDetails || []).map((base) => String(base._id)) : [],
    // Absent on the document means "every tab", which is why the switch is a
    // separate flag rather than an empty list.
    restrictLegacyTabs: Boolean(user && Array.isArray(user.legacyTabs)),
    legacyTabs: user && Array.isArray(user.legacyTabs) ? user.legacyTabs.map(String) : [],
    restrictPlayEngineTabs: Boolean(user && Array.isArray(user.playEngineTabs)),
    playEngineTabs: user && Array.isArray(user.playEngineTabs) ? user.playEngineTabs.map(String) : [],
    restrictPe3dTabs: Boolean(user && Array.isArray(user.pe3dTabs)),
    pe3dTabs: user && Array.isArray(user.pe3dTabs) ? user.pe3dTabs.map(String) : [],
    companySwitchTargets: user ? (user.companySwitchTargets || []).map(String) : [],
    exportTargetCompanies: user ? (user.exportTargetCompanies || []).map(String) : [],
    manageRuntimeBases: Boolean(user && user.manageRuntimeBases === true),
    grantBasesToCompanies: Boolean(user && user.grantBasesToCompanies === true),
    grantPe3dGames: Boolean(user && user.grantPe3dGames === true),
  });

  const loadCompanyData = async (targetCompanyID) => {
    const [userResult, baseResult] = await Promise.all([
      findUsers(isSuper ? { companyID: targetCompanyID } : {}),
      findAllBases(isSuper ? { companyID: targetCompanyID } : {}),
    ]);

    if (!userResult.ok) {
      props.createSnackbar(`Could not load users: ${userResult.data || userResult.status}`, "error", false);
      setUsers([]);
    } else {
      setUsers(userResult.data || []);
    }
    setCompanyBases(baseResult.ok ? baseResult.data || [] : []);
    setSelectedUserID(null);
    setDraft(null);
    setIsLoading(false);
  };

  useEffect(() => {
    const boot = async () => {
      if (isSuper) {
        const companyResult = await findCompanies({});
        const list = companyResult.ok ? companyResult.data || [] : [];
        setCompanies(list);
        const first = homeCompanyID || (list[0] && list[0]._id) || null;
        setCompanyID(first);
        await loadCompanyData(first);
        return;
      }
      await loadCompanyData(homeCompanyID);
    };
    boot();
  }, []);

  const selectCompany = async (nextCompanyID) => {
    setCompanyID(nextCompanyID);
    setIsLoading(true);
    await loadCompanyData(nextCompanyID);
  };

  const selectUser = (user) => {
    setSelectedUserID(String(user._id));
    setDraft(draftFromUser(user));
  };

  const startNewUser = () => {
    setSelectedUserID(NEW_USER);
    setDraft(draftFromUser(null));
    // The username lives on the first tab and is the only required new field.
    setActiveTab(TAB.ACCOUNT);
  };

  const patchDraft = (patch) => setDraft((current) => ({ ...current, ...patch }));

  const togglePermission = (key) => {
    const has = draft.permissions.indexOf(key) !== -1;
    patchDraft({
      permissions: has ? draft.permissions.filter((p) => p !== key) : [...draft.permissions, key],
    });
  };

  // A form encoder cannot send an empty array — appending no values just omits
  // the field, which the server reads as "leave it alone". An empty string is
  // the agreed marker for "set this to nothing".
  const listParam = (values) => (values.length > 0 ? values : "");

  // Same rules and same messages as before; the tab holding the offending field
  // is brought forward, since it may not be the one being looked at.
  const refuse = (message, tab) => {
    setActiveTab(tab);
    props.createSnackbar(message, "warning", false);
  };

  const saveUser = async () => {
    if (!draft) return;

    if (selectedUserID === NEW_USER) {
      if (draft.username.trim().length < 2) {
        refuse("A username needs at least 2 characters.", TAB.ACCOUNT);
        return;
      }
      if (draft.password.length < 8) {
        refuse("A password needs at least 8 characters.", TAB.ACCOUNT);
        return;
      }
    }
    if (draft.permissions.length === 0) {
      refuse("Give the user at least one permission.", TAB.PERMISSIONS);
      return;
    }
    if (draft.restrictBases && draft.bases.length === 0) {
      refuse("A restricted user with no bases can see nothing. Pick at least one.", TAB.BASES);
      return;
    }
    if (draft.restrictLegacyTabs && draft.legacyTabs.length === 0) {
      refuse("Leave at least one legacy editor tab, or the editor opens empty.", TAB.EDITOR_TABS);
      return;
    }
    if (draft.restrictPlayEngineTabs && draft.playEngineTabs.length === 0) {
      refuse("Leave at least one playEngine editor tab, or the editor opens empty.", TAB.EDITOR_TABS);
      return;
    }
    if (draft.restrictPe3dTabs && draft.pe3dTabs.length === 0) {
      refuse("Leave at least one pe3d editor tab, or the editor opens empty.", TAB.EDITOR_TABS);
      return;
    }

    setIsSaving(true);
    const shared = {
      role: draft.role,
      permissions: draft.permissions,
      restrictBases: draft.restrictBases ? undefined : "false",
      bases: draft.restrictBases ? listParam(draft.bases) : undefined,
      companySwitchTargets: isSuper ? listParam(draft.companySwitchTargets) : undefined,
      exportTargetCompanies: isSuper ? listParam(draft.exportTargetCompanies) : undefined,
      manageRuntimeBases: isSuper ? String(draft.manageRuntimeBases) : undefined,
      grantBasesToCompanies: isSuper ? String(draft.grantBasesToCompanies) : undefined,
      grantPe3dGames: isSuper ? String(draft.grantPe3dGames) : undefined,
      // Only a superAdmin may set these, so a companyAdmin sends nothing at all
      // rather than sending "unrestricted" and having the server ignore it.
      restrictLegacyTabs: isSuper && !draft.restrictLegacyTabs ? "false" : undefined,
      legacyTabs: isSuper && draft.restrictLegacyTabs ? listParam(draft.legacyTabs) : undefined,
      restrictPlayEngineTabs: isSuper && !draft.restrictPlayEngineTabs ? "false" : undefined,
      playEngineTabs: isSuper && draft.restrictPlayEngineTabs ? listParam(draft.playEngineTabs) : undefined,
      restrictPe3dTabs: isSuper && !draft.restrictPe3dTabs ? "false" : undefined,
      pe3dTabs: isSuper && draft.restrictPe3dTabs ? listParam(draft.pe3dTabs) : undefined,
    };

    const result =
      selectedUserID === NEW_USER
        ? await insertUser({
          ...shared,
          restrictBases: undefined,
          restrictLegacyTabs: undefined,
          restrictPlayEngineTabs: undefined,
          restrictPe3dTabs: undefined,
          username: draft.username.trim(),
          password: draft.password,
          companyID: isSuper ? companyID : undefined,
        })
        : await updateUser({
          ...shared,
          userID: selectedUserID,
          password: draft.password ? draft.password : undefined,
        });

    setIsSaving(false);
    if (!result.ok) {
      props.createSnackbar(`Could not save: ${result.data || result.status}`, "error", false);
      return;
    }
    props.createSnackbar(selectedUserID === NEW_USER ? "User created." : "User updated.", "success", false);
    setIsLoading(true);
    await loadCompanyData(companyID);
  };

  const removeUser = async (user) => {
    const result = await deleteUser({ userID: String(user._id) });
    if (!result.ok) {
      props.createSnackbar(`Could not delete: ${result.data || result.status}`, "error", false);
      return;
    }
    props.createSnackbar(`${user.username} deleted.`, "success", false);
    setIsLoading(true);
    await loadCompanyData(companyID);
  };

  if (isLoading) {
    return (
      <div className={classes.root}>
        <div className={classes.centred}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  const isNew = selectedUserID === NEW_USER;
  const otherCompanies = companies.filter((company) => String(company._id) !== String(companyID));

  // Version Transfer and Version Update are Playin's own; a company without the
  // flag is not shown the checkboxes at all. A superAdmin reads the flag off the
  // company it picked, a companyAdmin off its own session.
  const editedCompany = companies.find((company) => String(company._id) === String(companyID)) || null;
  const hasInternalTools = editedCompany
    ? editedCompany.hasInternalTools === true
    : localStorage.getItem("user.hasInternalTools") === "true";

  const roleChipFor = (role) => {
    if (role === "superAdmin") return { className: `${classes.chip} ${classes.chipFilled}`, label: "super" };
    if (role === "companyAdmin") return { className: `${classes.chip} ${classes.chipOutline}`, label: "admin" };
    return null;
  };

  const permissionBox = (option) => (
    <div key={option.key} className={classes.checkRow} onClick={() => togglePermission(option.key)}>
      <Checkbox
        size="small"
        checked={draft.permissions.indexOf(option.key) !== -1}
        onChange={() => togglePermission(option.key)}
      />
      <span className={classes.checkText}>
        <span className={classes.checkLabel}>{option.label}</span>
        <span className={classes.checkHint}>{option.hint}</span>
      </span>
    </div>
  );

  // The tab set is the same for both admin tiers apart from the cross-company
  // lists and the editor-tab narrowing, which only a superAdmin can hand out.
  const baseSummary = draft && draft.restrictBases ? String(draft.bases.length) : "all";

  const toggleEditorTab = (field, key) => {
    const current = draft[field] || [];
    const has = current.indexOf(key) !== -1;
    patchDraft({ [field]: has ? current.filter((tab) => tab !== key) : [...current, key] });
  };

  /** One tab set: the "show everything" switch, and the list when it is off. */
  // Turning the switch ON starts from the tabs the user can see right now, so a
  // superAdmin adds or removes one rather than rebuilding the list from nothing.
  const startNarrowing = (options, field, switchField) => {
    const current = draft[field] || [];
    patchDraft({
      [switchField]: !draft[switchField],
      [field]:
        !draft[switchField] && current.length === 0
          ? options.filter((option) => option.key !== "agent").map((option) => option.key)
          : current,
    });
  };

  const editorTabSet = (title, hint, options, field, switchField) => (
    <div className={classes.splitColumn}>
      <Typography className={classes.subTitle}>{title}</Typography>
      <Typography className={classes.hint}>{hint}</Typography>
      <div className={classes.checkRow} onClick={() => startNarrowing(options, field, switchField)}>
        <Checkbox
          size="small"
          checked={draft[switchField]}
          onChange={() => startNarrowing(options, field, switchField)}
        />
        <span className={classes.checkText}>
          <span className={classes.checkLabel}>Show only the ticked tabs</span>
          <span className={classes.checkHint}>
            {draft[switchField]
              ? "Everything else is hidden."
              : "Every tab except Agent, which is always ticked on."}
          </span>
        </span>
      </div>
      {draft[switchField] && (
        <div className={classes.tabChecklist}>
          {options.map((option) => (
            <div
              key={option.key}
              className={classes.checkRow}
              onClick={() => toggleEditorTab(field, option.key)}
            >
              <Checkbox
                size="small"
                checked={(draft[field] || []).indexOf(option.key) !== -1}
                onChange={() => toggleEditorTab(field, option.key)}
              />
              <span className={classes.checkText}>
                <span className={classes.checkLabel}>{option.label}</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // "default" rather than "all": Agent is opt-in, so an unnarrowed user does not
  // see every tab (see modules/editorTabAccess).
  const editorTabSummary = () => {
    const legacy = draft.restrictLegacyTabs ? draft.legacyTabs.length : "default";
    const playEngine = draft.restrictPlayEngineTabs ? draft.playEngineTabs.length : "default";
    const pe3d = draft.restrictPe3dTabs ? draft.pe3dTabs.length : "default";
    return `${legacy} / ${playEngine} / ${pe3d}`;
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography className={classes.title}>User management</Typography>
        {isSuper && (
          <TextField
            select
            size="small"
            label="Company"
            className={classes.headerSelect}
            value={String(companyID || "")}
            onChange={(event) => selectCompany(event.target.value)}
          >
            {companies.map((company) => (
              <MenuItem key={company._id} value={String(company._id)}>
                {company.name}
              </MenuItem>
            ))}
          </TextField>
        )}
        <div className={classes.headerSpacer} />
        <Button variant="contained" color="primary" onClick={startNewUser}>
          + Add a user
        </Button>
      </div>

      <div className={classes.body}>
        <div className={classes.listColumn}>
          <div className={classes.listHeader}>
            <div className={classes.listLabel}>Users ({users.length})</div>
          </div>
          <div className={classes.listScroller}>
            {users.map((user) => {
              const chip = roleChipFor(user.role);
              return (
                <div
                  key={String(user._id)}
                  className={`${classes.listRow} ${
                    String(user._id) === String(selectedUserID) ? classes.listRowSelected : ""
                  }`}
                  onClick={() => selectUser(user)}
                >
                  <div>
                    <div className={classes.rowTitle}>
                      {user.username}
                      {chip && <span className={chip.className}>{chip.label}</span>}
                    </div>
                    <div className={classes.rowMeta}>
                      {user.isBaseRestricted
                        ? `${(user.basesWithDetails || []).length} of ${companyBases.length} bases`
                        : `all ${companyBases.length} bases`}
                      {" · "}
                      {(user.permissions || []).length} permissions
                    </div>
                  </div>
                  {user.username !== props.userName && (
                    <IconButton
                      size="small"
                      title={`Delete ${user.username}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        removeUser(user);
                      }}
                    >
                      <DeleteIcon fontSize="small" style={{ color: DANGER }} />
                    </IconButton>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className={classes.detailColumn}>
          {!draft && <div className={classes.detailEmpty}>Select a user, or add one.</div>}
          {draft && (
            <>
              <Typography className={classes.detailTitle}>{isNew ? "New user" : draft.username}</Typography>

              <Tabs
                className={classes.tabsBar}
                value={activeTab}
                onChange={(event, next) => setActiveTab(next)}
                indicatorColor="primary"
                textColor="inherit"
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab className={classes.tab} label="Account" />
                <Tab className={classes.tab} label={`Permissions · ${draft.permissions.length}`} />
                <Tab className={classes.tab} label={`Bases · ${baseSummary}`} />
                {isSuper && <Tab className={classes.tab} label={`Editor tabs · ${editorTabSummary()}`} />}
                {isSuper && (
                  <Tab
                    className={classes.tab}
                    label={`Companies · ${draft.companySwitchTargets.length} / ${draft.exportTargetCompanies.length}`}
                  />
                )}
              </Tabs>

              <div className={classes.tabBody}>
                {activeTab === TAB.ACCOUNT && (
                  <>
                    {isNew && (
                      <TextField
                        className={classes.field}
                        size="small"
                        label="Username"
                        value={draft.username}
                        onChange={(event) => patchDraft({ username: event.target.value })}
                      />
                    )}
                    {/* Deliberately not masked: an admin is setting a password for
                        somebody else and has to read it back to pass it on. Their
                        own password is still masked, in login and Change Password. */}
                    <TextField
                      className={classes.field}
                      size="small"
                      label={isNew ? "Password" : "New password (leave blank to keep)"}
                      value={draft.password}
                      onChange={(event) => patchDraft({ password: event.target.value })}
                    />
                    <TextField
                      select
                      className={classes.field}
                      size="small"
                      label="Role"
                      value={draft.role}
                      onChange={(event) => patchDraft({ role: event.target.value })}
                      helperText={(ROLE_OPTIONS.find((option) => option.key === draft.role) || {}).hint}
                    >
                      {ROLE_OPTIONS.filter((option) => option.key !== "superAdmin" || isSuper).map((option) => (
                        <MenuItem key={option.key} value={option.key}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </>
                )}

                {activeTab === TAB.PERMISSIONS && (
                  <>
                    <Typography className={classes.hint}>
                      What the user may do. Independent of the role — an admin with no permissions still
                      cannot edit anything.
                    </Typography>
                    <div className={classes.permissionGrid}>{PERMISSION_OPTIONS.map(permissionBox)}</div>

                    {hasInternalTools && (
                      <div className={classes.internalBlock}>
                        <Typography className={classes.internalTitle}>Playin internal</Typography>
                        <Typography className={classes.hint}>
                          Neither of these stays inside the company: Version Update rewrites every version
                          of whichever base it is given, and can be pointed at another environment.
                        </Typography>
                        <div className={classes.permissionGrid}>
                          {INTERNAL_PERMISSION_OPTIONS.map(permissionBox)}
                        </div>
                      </div>
                    )}

                    {/* Not a permission: companyAdmins hand those out, and a base
                        is not scoped to one company (see user/roles.js). */}
                    {isSuper && (
                      <div className={classes.internalBlock}>
                        <Typography className={classes.internalTitle}>Granted by a superAdmin</Typography>
                        <div
                          className={classes.checkRow}
                          onClick={() => patchDraft({ manageRuntimeBases: !draft.manageRuntimeBases })}
                        >
                          <Checkbox
                            size="small"
                            checked={draft.role === "superAdmin" || draft.manageRuntimeBases}
                            disabled={draft.role === "superAdmin"}
                            onChange={() => patchDraft({ manageRuntimeBases: !draft.manageRuntimeBases })}
                          />
                          <span className={classes.checkText}>
                            <span className={classes.checkLabel}>Create runtime bases &amp; games</span>
                            <span className={classes.checkHint}>
                              {draft.role === "superAdmin"
                                ? "A superAdmin always can."
                                : "Create games and playEngine bases. New bases are granted to the user and their company; only superAdmins can delete."}
                            </span>
                          </span>
                        </div>
                        <div
                          className={classes.checkRow}
                          onClick={() => patchDraft({ grantBasesToCompanies: !draft.grantBasesToCompanies })}
                        >
                          <Checkbox
                            size="small"
                            checked={draft.role === "superAdmin" || draft.grantBasesToCompanies}
                            disabled={draft.role === "superAdmin"}
                            onChange={() => patchDraft({ grantBasesToCompanies: !draft.grantBasesToCompanies })}
                          />
                          <span className={classes.checkText}>
                            <span className={classes.checkLabel}>Grant bases to companies</span>
                            <span className={classes.checkHint}>
                              {draft.role === "superAdmin"
                                ? "A superAdmin always can."
                                : "Open any base to any company, or take it away, from the Companies screen. Company settings stay with superAdmins."}
                            </span>
                          </span>
                        </div>
                        <div
                          className={classes.checkRow}
                          onClick={() => draft.role !== "superAdmin" && patchDraft({ grantPe3dGames: !draft.grantPe3dGames })}
                        >
                          <Checkbox
                            size="small"
                            checked={draft.role === "superAdmin" || draft.grantPe3dGames}
                            disabled={draft.role === "superAdmin"}
                            onChange={() => patchDraft({ grantPe3dGames: !draft.grantPe3dGames })}
                          />
                          <span className={classes.checkText}>
                            <span className={classes.checkLabel}>Grant pe3d games to bases</span>
                            <span className={classes.checkHint}>
                              {draft.role === "superAdmin"
                                ? "A superAdmin always can."
                                : "Chooses which PlayEngine3D games a pe3d base offers for new variants, on the bases the user reaches. Any imported game can be chosen, so this is for Playin's own people."}
                            </span>
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {activeTab === TAB.BASES && (
                  <>
                    <div
                      className={classes.checkRow}
                      onClick={() => patchDraft({ restrictBases: !draft.restrictBases })}
                    >
                      <Checkbox
                        size="small"
                        checked={draft.restrictBases}
                        onChange={() => patchDraft({ restrictBases: !draft.restrictBases })}
                      />
                      <span className={classes.checkText}>
                        <span className={classes.checkLabel}>Restrict this user to selected bases</span>
                        <span className={classes.checkHint}>
                          {draft.restrictBases
                            ? "Only the bases ticked below, and only while the company still holds them."
                            : `Every base the company holds — currently ${companyBases.length}.`}
                        </span>
                      </span>
                    </div>
                    {draft.restrictBases && (
                      <SelectorBases
                        fill
                        bases={companyBases}
                        selectedBaseIDs={draft.bases}
                        onChange={(bases) => patchDraft({ bases })}
                        emptyText="This company holds no bases yet."
                      />
                    )}
                  </>
                )}

                {activeTab === TAB.EDITOR_TABS && isSuper && (
                  <>
                    <Typography className={classes.hint}>
                      Which panels the version editor shows this user. The three sets are separate because a
                      legacy playable, a playEngine base and a pe3d base do not have the same editor. Left alone, a user
                      sees every tab except <b>Agent</b> — that one is ticked on for the people who should
                      have it. This hides panels; it does not withhold the data behind them.
                    </Typography>
                    <div className={classes.splitColumns}>
                      {editorTabSet(
                        "Legacy game versions",
                        "Normal playables and scene-editor bases.",
                        LEGACY_TAB_OPTIONS,
                        "legacyTabs",
                        "restrictLegacyTabs"
                      )}
                      {editorTabSet(
                        "PlayEngine versions",
                        "Bases running the playEngine.",
                        PLAY_ENGINE_TAB_OPTIONS,
                        "playEngineTabs",
                        "restrictPlayEngineTabs"
                      )}
                      {editorTabSet(
                        "PE3D variants",
                        "pe3d bases. Their Agent tab follows the PlayEngine set's Agent.",
                        PE3D_TAB_OPTIONS,
                        "pe3dTabs",
                        "restrictPe3dTabs"
                      )}
                    </div>
                  </>
                )}

                {activeTab === TAB.COMPANIES && isSuper && (
                  <div className={classes.splitColumns}>
                    <div className={classes.splitColumn}>
                      <Typography className={classes.subTitle}>Can view the dashboard as</Typography>
                      <Typography className={classes.hint}>
                        Lets this user look at another company&apos;s content. It does not make them an
                        admin there.
                      </Typography>
                      <SelectorCompanies
                        companies={otherCompanies}
                        selectedCompanyIDs={draft.companySwitchTargets}
                        onChange={(companySwitchTargets) => patchDraft({ companySwitchTargets })}
                        emptyText="No other company."
                      />
                    </div>
                    <div className={classes.splitColumn}>
                      <Typography className={classes.subTitle}>Can export snapshots into</Typography>
                      <Typography className={classes.hint}>
                        A separate list: exporting a snapshot as a version into a company is not the same as
                        being able to look at it.
                      </Typography>
                      <SelectorCompanies
                        companies={otherCompanies}
                        selectedCompanyIDs={draft.exportTargetCompanies}
                        onChange={(exportTargetCompanies) => patchDraft({ exportTargetCompanies })}
                        emptyText="No other company."
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className={classes.footer}>
                <Button variant="contained" color="primary" disabled={isSaving} onClick={saveUser}>
                  {isNew ? "Create user" : "Save changes"}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => (isNew ? setDraft(draftFromUser(null)) : setDraft(draftFromUser(selectedUser)))}
                >
                  Reset
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
